import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Paires libellé / valeur.
 * - `KvList` + `KvRow` (`kv-list` / `kv-row`) : liste verticale à filets,
 *   libellé `muted-foreground` à gauche, valeur à droite (13px). Résumés,
 *   détails d'une fiche.
 * - `Kv` + `KvItem` (`kv` : `<dl>` en grille de colonnes de 170px) :
 *   libellé 12px au-dessus de la valeur 14px. Caractéristiques d'une tentative,
 *   d'un cas d'usage.
 */

function KvList({ className, ...props }: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  return <div className={cn('grid', className)} {...props} />;
}

interface KvRowProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  label: React.ReactNode;
  children: React.ReactNode;
  /** Valeur en mono (montant, identifiant). */
  mono?: boolean;
}

function KvRow({
  label,
  children,
  mono = false,
  className,
  ...props
}: KvRowProps): React.JSX.Element {
  return (
    <div
      className={cn(
        'flex items-baseline justify-between gap-3 border-b border-border py-2 text-[13px] leading-[18px] last:border-b-0',
        className,
      )}
      {...props}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('min-w-0 text-right', mono && 'font-mono tabular-nums')}>{children}</span>
    </div>
  );
}

function Kv({ className, ...props }: React.HTMLAttributes<HTMLDListElement>): React.JSX.Element {
  return (
    <dl
      className={cn(
        'm-0 grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-x-6 gap-y-4',
        className,
      )}
      {...props}
    />
  );
}

interface KvItemProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  label: React.ReactNode;
  children: React.ReactNode;
  mono?: boolean;
}

function KvItem({
  label,
  children,
  mono = false,
  className,
  ...props
}: KvItemProps): React.JSX.Element {
  return (
    <div className={cn('min-w-0', className)} {...props}>
      <dt className="mb-0.5 text-label text-muted-foreground">{label}</dt>
      <dd className={cn('m-0 text-sm [overflow-wrap:anywhere]', mono && 'font-mono tabular-nums')}>
        {children}
      </dd>
    </div>
  );
}

export { KvList, KvRow, Kv, KvItem };
